import { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, Label } from './StatsUI';

// "By Person" card with month arrows. The current month is already in the stats
// summary payload, so it renders instantly and only older months cost a request
// (<base>/stats/by-person?month=YYYY-MM).
//
// variant 'count'   — { name, count }              (CAD, Geospatial)
// variant 'records' — { name, complete, in_progress, pending }
//   In progress and pending are current state, not history, so they're only
//   meaningful — and only returned — for the current month.

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export default function ByPersonCard({ base, variant = 'count', initialRows }) {
  const [offset, setOffset] = useState(0); // 0 = current month, -1 = previous, …
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const now = new Date();
  const monthDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const month = ym(monthDate);
  const isCurrent = offset === 0;

  useEffect(() => {
    if (isCurrent) { setData(null); setError(''); setLoading(false); return undefined; }
    let stale = false;
    setLoading(true);
    setError('');
    api.get(`${base}/stats/by-person?month=${month}`)
      .then((res) => { if (!stale) setData(res.data); })
      .catch(() => { if (!stale) setError("Couldn't load that month."); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [base, month, isCurrent]);

  const rows = (isCurrent ? initialRows : data?.rows) || [];
  const total = rows.reduce((s, r) => s + Number(variant === 'records' ? r.complete : r.count), 0);

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <Label>By Person</Label>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setOffset(offset - 1)}
            className="text-gray-400 hover:text-white px-1.5 leading-none text-sm"
            title="Previous month"
          >‹</button>
          <span className="text-[11px] font-bold text-gray-300 min-w-[6.5rem] text-center">
            {monthDate.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
            {isCurrent && <span className="text-gray-600 font-normal"> · now</span>}
          </span>
          <button
            onClick={() => setOffset(Math.min(0, offset + 1))}
            disabled={isCurrent}
            className="text-gray-400 hover:text-white px-1.5 leading-none text-sm disabled:opacity-30"
            title="Next month"
          >›</button>
        </div>
      </div>

      {loading && <p className="text-gray-500 text-sm mt-3">Loading…</p>}
      {error && <p className="text-red-400 text-sm mt-3">{error}</p>}

      {!loading && !error && (
        rows.length === 0 ? (
          <p className="text-gray-500 text-sm mt-3">
            No completions {isCurrent ? 'this month yet' : 'that month'}.
          </p>
        ) : variant === 'records' ? (
          <div className="mt-3 space-y-2">
            {rows.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <span className="text-gray-300 w-20 truncate" title={p.name}>{p.name}</span>
                <div className="flex gap-3">
                  <span className="text-green-400">{p.complete} done</span>
                  {isCurrent && <>
                    <span className="text-blue-400">{p.in_progress} active</span>
                    <span className="text-gray-500">{p.pending} pending</span>
                  </>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <PersonBars rows={rows} total={total} />
        )
      )}

      {!isCurrent && !loading && !error && variant === 'records' && rows.length > 0 && (
        <p className="text-[11px] text-gray-600 mt-3">
          Active and pending counts are current state, so they're only shown for this month.
        </p>
      )}
    </Card>
  );
}

function PersonBars({ rows = [], total }) {
  return (
    <div className="mt-3 space-y-2">
      {rows.map((p) => {
        const pct = Math.round((Number(p.count) / (total || 1)) * 100);
        return (
          <div key={p.name}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-300 truncate" title={p.name}>{p.name}</span>
              <span className="text-gray-400">{p.count} <span className="text-gray-600">({pct}%)</span></span>
            </div>
            <div className="h-1.5 rounded-full" style={{ backgroundColor: '#1e3a5f' }}>
              <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: '#c9a84c' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
